import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type {
	HouseholdMember,
	Todo,
	TodoCategory,
	TodoTitle,
	UserId,
} from '@hewey/shared'
import { colorFor } from '@utils/colors.ts'
import { useOnline } from '@hooks/useOnline.ts'
import type { BoardColumn } from './Board.tsx'
import { TodoItem } from './TodoItem.tsx'
import styles from './Board.module.css'

type Props = {
	columns: BoardColumn[]
	members: HouseholdMember[]
	memberMap: Map<UserId, HouseholdMember>
	onToggle: (todo: Todo, next: boolean) => void
	onRename: (todo: Todo, title: TodoTitle) => void
	onTagClick: (todo: Todo) => void
	onPriorityToggle: (todo: Todo) => void
	onAssign: (todo: Todo, assigneeIds: UserId[]) => void
	onClearCompleted: () => void
}

// The "priority only" view: one column collecting starred todos across every
// category. Active cards are grouped under per-category sub-headings (reusing
// the board's category order), with a single collapsible Done section at the
// bottom — like a normal lane, but spanning categories.
export const PriorityColumn = ({
	columns,
	members,
	memberMap,
	onToggle,
	onRename,
	onTagClick,
	onPriorityToggle,
	onAssign,
	onClearCompleted,
}: Props) => {
	const online = useOnline()
	const [showDone, setShowDone] = useState(false)

	const groups = useMemo(
		() =>
			columns
				.map((c) => ({
					category: c.category,
					active: c.todos.filter((t) => t.priority && !t.completed),
				}))
				.filter((g) => g.active.length > 0),
		[columns],
	)

	const done = useMemo(
		() =>
			columns
				.flatMap((c) => c.todos)
				.filter((t) => t.priority && t.completed)
				.sort((a, b) =>
					(b.completedAt ?? '').localeCompare(a.completedAt ?? ''),
				),
		[columns],
	)

	const activeCount = useMemo(
		() => groups.reduce((n, g) => n + g.active.length, 0),
		[groups],
	)

	const itemProps = (todo: Todo) => ({
		todo,
		members,
		memberMap,
		onToggle: (next: boolean) => onToggle(todo, next),
		onRename: (title: TodoTitle) => onRename(todo, title),
		onTagClick: () => onTagClick(todo),
		onPriorityToggle: () => onPriorityToggle(todo),
		onAssign: (ids: UserId[]) => onAssign(todo, ids),
	})

	const subHeading = (category: TodoCategory | null) => (
		<header className={styles.subHeader}>
			<span
				className={styles.dot}
				style={{
					background: category
						? colorFor(category)
						: 'var(--ink-muted)',
				}}
				aria-hidden="true"
			/>
			<h3 className={styles.subName}>{category ?? 'general'}</h3>
		</header>
	)

	return (
		<div className={styles.priorityWrap}>
			<section className={styles.column}>
				<header className={styles.colHeader}>
					<span
						className={styles.dot}
						style={{ background: 'var(--accent-sunshine)' }}
						aria-hidden="true"
					/>
					<h2 className={styles.colName}>priority</h2>
					<span className={styles.count}>{activeCount}</span>
				</header>

				<div className={styles.cards}>
					{groups.map((g) => (
						<div key={g.category ?? '__general'}>
							{subHeading(g.category)}
							<ul className={styles.list}>
								{g.active.map((todo) => (
									<TodoItem
										key={todo.id}
										{...itemProps(todo)}
									/>
								))}
							</ul>
						</div>
					))}

					{activeCount === 0 && done.length === 0 && (
						<p className={styles.colEmpty}>
							No priority cards. Tap the ☆ on a todo to flag it.
						</p>
					)}

					{done.length > 0 && (
						<div className={styles.done}>
							<button
								type="button"
								className={styles.doneToggle}
								onClick={() => setShowDone((s) => !s)}
								aria-expanded={showDone}
							>
								{showDone ? (
									<ChevronDown size={16} aria-hidden="true" />
								) : (
									<ChevronRight
										size={16}
										aria-hidden="true"
									/>
								)}
								Done ({done.length})
							</button>

							{showDone && (
								<>
									<ul className={styles.list}>
										{done.map((todo) => (
											<TodoItem
												key={todo.id}
												{...itemProps(todo)}
											/>
										))}
									</ul>
									<button
										type="button"
										className={styles.clear}
										onClick={onClearCompleted}
										disabled={!online}
									>
										Clear completed
									</button>
								</>
							)}
						</div>
					)}
				</div>
			</section>
		</div>
	)
}
